import { defineStore } from 'pinia'
import { getSessionVersion } from '@/auth/storage'
import { useEnrollmentsStore } from '@/stores/enrollments'
import {
  fetchProgress as svcFetchProgress,
  updateVideoProgress as svcUpdateVideoProgress,
  patchProgress as svcPatchProgress,
  createProgress as svcCreateProgress,
} from '@/services/progress'
import {
  resetEnrollmentProgress as svcResetEnrollmentProgress,
  trackEnrollmentAccess as svcTrackEnrollmentAccess,
} from '@/services/enrollments'

export const useProgressStore = defineStore('progress', {
  state: () => ({
    items: [],
    total: 0,
    page: 1,
    pageSize: 20,
    loaded: false,
    loading: false,
    error: null,
    lastLoadedAt: 0,
    _inFlight: {}, // key: `${enrollmentId}:${cmsLessonId}`
    lastResetAt: 0,
  }),
  getters: {
    forEnrollment: (state) => (enrollmentId) => state.items.filter(p => p.enrollment === enrollmentId),
    completedSet: (state) => (enrollmentId) => {
      const set = new Set()
      state.items.forEach(p => {
        if (p.enrollment === enrollmentId) {
          const lessonId = p.cms_lesson_id ?? p.lesson
          const isComplete = p.completed === true || !!p.completed_on || p.status === 'completed' || p.lesson_completed === true
          if (lessonId && isComplete) set.add(lessonId)
        }
      })
      return set
    },
  },
  actions: {
    async loadIfNeeded(force = false) {
      const version = getSessionVersion()
      const now = Date.now()
      const TTL = 2 * 60 * 1000 // 2 minutes
      if (!force && this.loaded && this.lastLoadedAt && (now - this.lastLoadedAt) < TTL) return
      this.loading = true
      this.error = null
      try {
        const data = await svcFetchProgress()
        if (version !== getSessionVersion()) return
        if (Array.isArray(data)) {
          this.items = data
          this.total = data.length
        } else {
          this.items = data?.results || []
          this.total = data?.count || this.items.length
        }
        this.lastLoadedAt = Date.now()
        this.loaded = true
      } catch (_e) {
        this.error = _e
      } finally {
        this.loading = false
      }
    },
    async loadPage(page = 1, pageSize = null, extra = {}) {
      const version = getSessionVersion()
      this.loading = true
      this.error = null
      try {
        const size = pageSize ?? this.pageSize
        const query = { page: String(page), page_size: String(size) }
        Object.entries(extra || {}).forEach(([k, v]) => {
          if (v !== undefined && v !== null && v !== '') query[k] = String(v)
        })
        const data = await svcFetchProgress(query)
        if (version !== getSessionVersion()) return
        if (Array.isArray(data)) {
          this.items = data
          this.total = data.length
        } else {
          this.items = data?.results || []
          this.total = data?.count || this.items.length
        }
        this.page = page
        this.pageSize = size
        this.lastLoadedAt = Date.now()
      } catch (_e) {
        this.error = _e
        throw _e
      } finally {
        this.loading = false
        if (version === getSessionVersion()) this.loaded = true
      }
    },

    async trackLessonAccess(enrollmentId, cmsLessonId) {
      try { await svcTrackEnrollmentAccess(enrollmentId, cmsLessonId) } catch (_e) {}
    },

    async updateVideoProgress(enrollmentId, cmsLessonId, progressSeconds, durationSeconds) {
      try {
        const res = await svcUpdateVideoProgress(cmsLessonId, progressSeconds, durationSeconds, enrollmentId)
        // Refresh caches if the backend marked the lesson as complete
        if (res?.lesson_completed) {
          const enrollments = useEnrollmentsStore()
          await Promise.all([
            this.loadIfNeeded(true),
            enrollments.loadIfNeeded(true)
          ])
        }
        return res || {}
      } catch (_e) {
        throw _e
      }
    },

    async markLessonComplete(enrollmentId, cmsLessonId) {
      const version = getSessionVersion()
      const key = `${enrollmentId}:${cmsLessonId}`
      if (this._inFlight[key]) return this._inFlight[key]
      const work = (async () => {
        await this.loadIfNeeded()
        const existing = this.items.find(p => p.enrollment === enrollmentId && (p.cms_lesson_id ?? p.lesson) === cmsLessonId)
        if (existing && (existing.completed || existing.completed_on)) return { success: true, already: true }
        const result = existing
          ? await svcPatchProgress(existing.id, { completed: true })
          : await svcCreateProgress({ enrollment: enrollmentId, cms_lesson_id: cmsLessonId, completed: true })
        if (version !== getSessionVersion()) throw new Error('Session changed during progress update')
        if (existing) this.items.splice(this.items.findIndex(p => p.id === existing.id), 1, result)
        else this.items.push(result)
        try { await useEnrollmentsStore().loadIfNeeded(true) } catch (_) {}
        return { success: true, updated: !!existing, created: !existing }
      })()
      this._inFlight[key] = work
      try { return await work } finally { delete this._inFlight[key] }
    },

    async resetCourseProgress(enrollmentId) {
      try {
        await svcResetEnrollmentProgress(enrollmentId)
        // Refresh both progress and enrollments caches
        const enrollments = useEnrollmentsStore()
        await Promise.all([
          this.loadIfNeeded(true),
          enrollments.loadIfNeeded(true)
        ])
        this.lastResetAt = Date.now()
        return { success: true }
      } catch (e) {
        this.error = e
        throw e
      }
    },

    clear() {
      this.items = []
      this.total = 0
      this.page = 1
      this.pageSize = 20
      this.loaded = false
      this.loading = false
      this.error = null
      this.lastLoadedAt = 0
      this._inFlight = {}
    }
  },
})
